import React from "react"
import Icon from "../Icon"
import { useNavigate } from "react-router-dom"

// Show the first name in full, then initials for any middle/last names:
// "Hassan Wycliffe Liuva" -> "Hassan W. L."
const abbreviateName = (fullName) => {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return parts[0] || ""
  const [first, ...rest] = parts
  return `${first} ${rest.map((p) => `${p[0].toUpperCase()}.`).join(" ")}`
}

function GroupsLayout({
  activeTab,
  onTabChange,
  onOpenCreate,
  invitesCount = 0,
  children,
  lockToDetailsOnly = false,
  currentUserName = "",
}) {
  const navigate = useNavigate()
  const tabs = [
    { id: "groups", label: "Start", icon: "list" },
    { id: "invites", label: "My Invitations", icon: "envelope" },
    { id: "archived", label: "Archived", icon: "archive" },
  ]
  const visibleTabs = lockToDetailsOnly ? [] : tabs

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 font-sans">
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="py-3 sm:py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              {!lockToDetailsOnly && (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm font-bold flex-shrink-0 shadow-md active:scale-95"
                  aria-label="Back to dashboard"
                  title="Back to dashboard"
                >
                  <Icon name="arrow-left" size="0.95rem" />
                </button>
              )}
              <div className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-sky-300 to-blue-300 rounded-xl shadow-md">
                <Icon name="people" size="1rem" className="text-white sm:w-5 sm:h-5" />
              </div>
              <div className="flex-shrink-0">
                <h1 className="text-sm sm:text-xl font-bold bg-gradient-to-r from-sky-500 to-blue-400 bg-clip-text text-transparent whitespace-nowrap">
                  Groups
                </h1>
                <p className="hidden sm:block text-[10px] sm:text-xs text-gray-500 whitespace-nowrap">
                  Group planning & contributions
                </p>
              </div>
            </div>

            {!lockToDetailsOnly && (
              <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
                {currentUserName && (
                  <div
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-sky-100 bg-sky-50/80 text-sky-700 text-[11px] sm:text-xs font-medium max-w-[150px] sm:max-w-[220px] md:max-w-[260px]"
                    title={currentUserName}
                  >
                    <Icon name="person-fill" size="0.8rem" className="flex-shrink-0" />
                    <span className="truncate">{abbreviateName(currentUserName)}</span>
                  </div>
                )}
                <button
                  onClick={onOpenCreate}
                  aria-label="New Group"
                  title="New Group"
                  className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-sky-400 text-white rounded-xl text-base font-bold hover:bg-sky-500 active:bg-sky-600 transition-colors shadow-md hover:shadow-lg"
                >
                  +
                </button>
              </div>
            )}
          </div>

          <div className="pt-1.5 pb-2 sm:pb-3 -mx-1 overflow-x-auto">
            <div className="inline-flex gap-1.5 px-1 min-w-full sm:min-w-0">
              {visibleTabs.map((tab) => {
                const badgeCount = Number(invitesCount) || 0
                const showInviteBadge = tab.id === "invites" && badgeCount > 0
                return (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className={`relative flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                      ? "bg-sky-400 text-white shadow-md"
                      : tab.id === "groups"
                        ? "bg-white/70 text-blue-600 hover:bg-blue-50 hover:shadow-sm"
                        : tab.id === "archived"
                          ? "bg-white/70 text-slate-600 hover:bg-slate-50 hover:shadow-sm"
                        : "bg-white/70 text-gray-600 hover:bg-white hover:shadow-sm"
                      }`}
                  >
                    <Icon name={tab.icon} size="0.9rem" />
                    <span className="text-xs sm:text-sm">{tab.label}</span>
                    {showInviteBadge && (
                      <span className="absolute -top-1 -right-1 z-10 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[18px] text-center shadow-md">
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {children}
      </main>
    </div>
  )
}

export default GroupsLayout
